import type { FC } from 'react';
import styled, { css } from 'styled-components';
import { Modal as AntdModal, type ModalProps as AntModalProps } from 'antd';
import { usePlatform } from '@/hooks/usePlatform';
import { Typography } from '../typography';
import { HeadlineLevel } from '../typography/enums/level';

const { Headline, Paragraph } = Typography;

export interface StyledModalProps extends AntModalProps {
  isMobile?: boolean;
  maxWidth?: number;
  description?: React.ReactNode;
}

const DEFAULT_MAX_WIDTH = 960;
const DEFAULT_MIN_WIDTH = 600;

const StyledModal = styled(AntdModal)<StyledModalProps>`
  min-width: ${({ maxWidth }) => (maxWidth && maxWidth <= DEFAULT_MIN_WIDTH ? maxWidth : DEFAULT_MIN_WIDTH)}px;
  max-width: ${({ maxWidth }) => maxWidth || DEFAULT_MAX_WIDTH}px;

  .ant-modal-header {
    border-bottom: 0;
    padding-bottom: 16px;
  }

  .ant-modal-title {
    font-weight: normal;
    padding-bottom: 16px;
  }

  .ant-modal-content {
    border-radius: 8px;
    max-height: 75vh;
    overflow-y: auto;
  }

  ${({ isMobile }) =>
    isMobile &&
    css`
      top: 5%;
      min-width: 300px;
    `}

  .ant-modal-footer {
    border-top: 0;
    & button {
      border-radius: 4px;
    }
  }
`;

export interface ModalProps extends StyledModalProps {
  /**
   * @deprecated Don't use this props, it's set by hook under the hood.
   */
  isMobile?: StyledModalProps['isMobile'];
  /**
   * @deprecated `max-width` in pixels should be set instead which is by default.
   */
  width?: StyledModalProps['width'];
}

export const Modal: FC<ModalProps> = props => {
  const { isMobile } = usePlatform();
  const {
    children,
    // NOTE: cause modal to take 100% of max-width
    width = '100%',
    isMobile: externalIsMobile,
    title,
    description,
    ...restProps
  } = props;

  const modalTitle =
    title || description ? (
      <>
        {title && <Headline level={HeadlineLevel.Three}>{title}</Headline>}
        {description && <Paragraph>{description}</Paragraph>}
      </>
    ) : undefined;

  return (
    <StyledModal
      closable={false}
      // NOTE: this maskClosable assign can be deleted after migrating to Antd5 since its false by default in v5
      maskClosable={false}
      width={width}
      title={modalTitle}
      {...restProps}
      isMobile={externalIsMobile ?? isMobile}
    >
      {children}
    </StyledModal>
  );
};
